'use client'

import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

interface HeroWordRotateProps {
  /** Static prefix that doesn't animate */
  prefix: string
  /** Words to cycle through after the prefix */
  words: string[]
  /** Render function for the final resolved state */
  renderFinal: () => ReactNode
  /** Time in ms each word is visible (default: 1500) */
  interval?: number
  /** Time in ms the first word is visible (default: 2500) */
  firstPhraseDelay?: number
  /** Class applied to cycling words */
  phraseClassName?: string
}

export function HeroWordRotate({
  prefix,
  words,
  renderFinal,
  interval = 1500,
  firstPhraseDelay = 2500,
  phraseClassName,
}: HeroWordRotateProps) {
  const [index, setIndex] = useState(0)
  const [resolved, setResolved] = useState(false)
  const prefersReducedMotion = useReducedMotion()

  const isResolved = resolved || prefersReducedMotion === true

  // Longest word determines stable container width
  const longestWord = useMemo(
    () => words.reduce((a, b) => (a.length > b.length ? a : b), ''),
    [words],
  )

  useEffect(() => {
    if (isResolved) return

    const delay = index === 0 ? firstPhraseDelay : interval
    const timer = setTimeout(() => {
      if (index >= words.length - 1) {
        setResolved(true)
      } else {
        setIndex((i) => i + 1)
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [index, isResolved, words.length, interval, firstPhraseDelay])

  return (
    <>
      {/* Screen reader: always show final state */}
      <span className="sr-only">{renderFinal()}</span>

      {/* Visual: animated content */}
      <span
        aria-hidden="true"
        className={`relative block overflow-hidden transition-[height] duration-500 ease-out ${
          isResolved ? 'h-[1.25em]' : 'h-[2.5em]'
        }`}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isResolved ? (
            <motion.span
              key="final"
              initial={{ y: '100%', opacity: 0, filter: 'blur(4px)' }}
              animate={{ y: '0%', opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-x-0 flex h-full items-center justify-center"
            >
              {renderFinal()}
            </motion.span>
          ) : (
            <motion.span
              key="cycling"
              exit={{ opacity: 0, filter: 'blur(4px)' }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-x-0 flex h-full flex-col items-center justify-center"
            >
              <span>{prefix}</span>
              <span className="relative inline-flex h-[1.25em] items-center overflow-hidden">
                {/* Invisible sizer — longest word keeps width stable */}
                <span className="invisible select-none">{longestWord}</span>
                {/* Rotating word */}
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={index}
                    initial={{ y: '100%', opacity: 0, filter: 'blur(3px)' }}
                    animate={{ y: '0%', opacity: 1, filter: 'blur(0px)' }}
                    exit={{ y: '-100%', opacity: 0, filter: 'blur(3px)' }}
                    transition={{
                      duration: 0.2,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className={`absolute inset-x-0 text-center ${phraseClassName ?? ''}`}
                  >
                    {words[index]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </>
  )
}
